import React, {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { Button, Input, Select as AntSelect, Tooltip, message as antdMessage } from "antd";
import {
  AudioMutedOutlined,
  AudioOutlined,
  BulbOutlined,
  CheckCircleOutlined,
  CloseOutlined,
  DashboardOutlined,
  GlobalOutlined,
  LoadingOutlined,
  PaperClipOutlined,
  SendOutlined,
  StopOutlined,
} from "@ant-design/icons";
import styled from "styled-components";

import { transcribeFacilityAiAudio } from "./api";
import useVoiceInput from "./useVoiceInput";
import useVisualViewportOffset from "./useVisualViewportOffset";

const { TextArea } = Input;

/* ComposerPanel (Wave 3.3) — the whole composer island, extracted verbatim
   from index.js so that:

   - typing a keystroke updates `draft` HERE (internal useState) and nothing
     else on the page re-renders (messages / sidebar / header stay put);
   - the push-to-talk recorder lives HERE too: useVoiceInput's elapsedMs is a
     useState ticking ~5x/second, so whichever component hosts the hook
     re-renders per tick. Hosting it in the page defeated every other
     optimization; hosting it here contains the ticks to this subtree (the
     mic button + Recording pill it feeds). The parent's global PTT keydown
     effect drives it through the imperative handle below (voiceStart /
     voiceStop / voiceCancel) and mirrors the status into a ref via
     onVoiceStatusChange — no parent re-renders involved;
   - the Wave 2 draft persistence (debounced save, scope flush, reset
     marker, unmount flush) moves in with the draft it protects. The exact
     localStorage key scheme and race fixes are preserved; the parent still
     owns the facility-switch wipe (clearFacilityDrafts) and restores drafts
     through the imperative setDraft/clearDraft methods at the exact same
     code points as before.

   Toolbar option states (webSearch / reasoningEffort / currentPageEnabled)
   intentionally stay in the parent: submitPrompt reads them at send time
   and they change on rare click events, not per keystroke — passing them as
   props keeps submitPrompt untouched and the props shallow-comparable. */

const Composer = styled.form`
  padding: 10px clamp(16px, 4vw, 56px) 18px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0), #ffffff 30%);
  border-top: 0;
`;

const ComposerInner = styled.div`
  box-sizing: border-box;
  width: min(768px, 100%);
  margin: 0 auto;
  padding: 12px 12px 8px;
  border: 1px solid var(--fai-border);
  border-radius: 20px;
  background: #ffffff;
  box-shadow: var(--fai-shadow-md);
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:focus-within {
    border-color: var(--fai-primary);
    box-shadow: var(--fai-shadow-md), var(--fai-focus-ring);
  }

  .ant-input {
    resize: none;
    border: 0;
    border-radius: 8px;
    background: transparent;
    box-shadow: none;
    color: #2f3b4c;
    font-size: 15px;
    line-height: 1.6;
    padding: 4px 6px;

    &:focus,
    &:hover {
      border-color: transparent;
      box-shadow: none;
    }
  }

  @media (max-width: 767px) {
    padding: 10px 10px 6px;
    border-radius: 16px;
  }
`;

const ComposerToolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 6px;

  /* E2E fix 3b: 12px between the options group and the action buttons
     overspends the 390px single-row budget; 8px keeps pills + actions on
     one row (final math in the review notes). */
  @media (max-width: 767px) {
    gap: 8px;
  }
`;

const ComposerOptions = styled.div`
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  min-width: 0;
`;

const WebSearchToggle = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px;
  border: 1px solid ${(props) => (props.$active ? "#d7e4f2" : "var(--fai-border)")};
  border-radius: 999px;
  background: ${(props) => (props.$active ? "var(--fai-primary-soft)" : "transparent")};
  color: ${(props) => (props.$active ? "var(--fai-primary)" : "var(--fai-text-muted)")};
  cursor: ${(props) => (props.$disabled ? "not-allowed" : "pointer")};
  font-size: 12px;
  font-weight: 600;
  line-height: 1.4;
  user-select: none;
  opacity: ${(props) => (props.$disabled ? 0.55 : 1)};
  transition: border-color 0.15s ease, color 0.15s ease, background 0.15s ease;

  &:hover {
    border-color: ${(props) => (props.$active ? "#c4d9ee" : "#cfd9e4")};
  }
`;

const ReasoningControl = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  min-height: 30px;
  padding-left: 9px;
  border: 1px solid var(--fai-border);
  border-radius: 999px;
  color: var(--fai-text-muted);
  background: #ffffff;

  .anticon {
    font-size: 12px;
  }

  /* E2E fix 3a: shed the pill chrome on mobile (border + wide left pad) —
     with the narrowed 26px select below the wrapper lands at 42px
     (2 pad + 12 bulb + 2 gap + 26 select), the budget the 390px toolbar
     math allows. The bulb icon and caret keep their own affordances, and
     the wrapping Tooltip still explains the control. */
  @media (max-width: 767px) {
    gap: 2px;
    padding-left: 2px;
    border: none;
  }
`;

const ReasoningSelect = styled(AntSelect)`
  width: 88px;
  font-size: 12px;

  .ant-select-selector {
    height: 28px !important;
    padding: 0 22px 0 3px !important;
    border: 0 !important;
    border-radius: 999px !important;
    box-shadow: none !important;
  }

  .ant-select-selection-item {
    line-height: 28px !important;
    font-weight: 600;
  }

  /* Mobile (P0 fix 4c): icon-only trigger — the selected-value label hides,
     leaving the bulb icon + caret as the compact dropdown trigger (the
     dropdown itself is unchanged; the currently selected option is
     highlighted inside it). E2E fix 3a narrows 34px → 26px: antd pins the
     caret at right: 11px regardless of padding, so the extra width was
     dead space; re-centering the caret (right: 7px in a 26px box) keeps
     the trigger readable while freeing 8px for the one-row budget. */
  @media (max-width: 767px) {
    width: 26px;

    .ant-select-selector {
      padding: 0 7px !important;
    }

    .ant-select-arrow {
      right: 7px !important;
    }

    .ant-select-selection-item {
      display: none;
    }
  }
`;

const ComposerActions = styled.div`
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 8px;

  /* E2E fix 3c: 3 × 38px buttons + 2 × 4px gaps = 122px on mobile (vs 130px
     at 8px gaps) — visual button size stays 38px for thumbs. */
  @media (max-width: 767px) {
    gap: 4px;
  }
`;

const VoiceIndicator = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px;
  border: 1px solid ${(props) => (props.$recording ? "#f2d6dd" : "var(--fai-border)")};
  border-radius: 999px;
  background: ${(props) => (props.$recording ? "var(--fai-danger-soft)" : "#ffffff")};
  color: ${(props) => (props.$recording ? "var(--fai-danger)" : "var(--fai-text-muted)")};
  font-size: 12px;
  font-weight: 600;
  line-height: 1.4;
  user-select: none;
`;

const VoiceDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--fai-danger);
  animation: facility-ai-voice-pulse 1s ease-in-out infinite;

  @keyframes facility-ai-voice-pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.3;
    }
  }
`;

/* Shared with index.js (the transcript's conversation-attachment chips). */
const AttachmentList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 8px;
`;

const AttachmentChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 280px;
  padding: 4px 8px 4px 10px;
  border: 1px solid ${(props) => (props.$failed ? "#f2d6dd" : "var(--fai-border)")};
  border-radius: 8px;
  background: ${(props) => (props.$failed ? "var(--fai-danger-soft)" : "var(--fai-surface-sidebar)")};
  color: ${(props) => (props.$failed ? "var(--fai-danger)" : "#2f3b4c")};
  font-size: 12px;
  font-weight: 600;
  line-height: 1.4;
  user-select: none;

  .facility-ai-attachment-name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .facility-ai-attachment-remove {
    /* P0 fix 7: the ::after overlay grows the tap area without inflating
       the chip (the visual X stays ~14px, the target becomes ~30px). */
    position: relative;
    display: inline-flex;
    border: 0;
    padding: 0;
    background: transparent;
    color: inherit;
    cursor: pointer;
    opacity: 0.7;

    &::after {
      content: "";
      position: absolute;
      inset: -8px;
    }

    &:hover {
      opacity: 1;
    }
  }
`;

const REASONING_EFFORT_OPTIONS = [
  { value: "none", label: "Off" },
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
  { value: "xhigh", label: "X-high" },
  { value: "max", label: "Max" },
];

/* Attachment validation is shared with index.js's handleAttachmentFiles —
   exported so the accepted-extension list and size cap stay defined once. */
const ATTACHMENT_EXTENSIONS = new Set([
  ".txt", ".md", ".markdown", ".csv", ".tsv", ".json", ".yaml", ".yml",
  ".xml", ".html", ".htm", ".log",
  ".pdf", ".docx", ".xlsx", ".xlsm",
  ".png", ".jpg", ".jpeg", ".webp", ".gif",
]);
const ATTACHMENT_ACCEPT = Array.from(ATTACHMENT_EXTENSIONS).join(",");
const ATTACHMENT_MAX_BYTES = 20 * 1024 * 1024;

/* Draft persistence (2.4, moved verbatim from index.js) — one composer draft
   per user + facility + conversation, keyed like the app's other
   facilitykpi:* storage entries. "anon" keeps drafts working pre-auth
   without leaking them across signed-in users. */
const DRAFT_STORAGE_PREFIX = "facilitykpi:ai:draft:";
const DRAFT_STORAGE_MAX_LENGTH = 4 * 1024; // chars → ≤ 8 KB as UTF-16
const DRAFT_SAVE_DELAY_MS = 300;

const draftStorageKey = (userId, facilityId, conversationId) =>
  `${DRAFT_STORAGE_PREFIX}${userId || "anon"}:${facilityId || ""}:${
    conversationId || "new"
  }`;

const readStoredDraft = (userId, facilityId, conversationId) => {
  try {
    const value = window.localStorage.getItem(
      draftStorageKey(userId, facilityId, conversationId)
    );
    return typeof value === "string" ? value : "";
  } catch (error) {
    return "";
  }
};

const writeStoredDraft = (userId, facilityId, conversationId, draft) => {
  const key = draftStorageKey(userId, facilityId, conversationId);
  try {
    if (!draft) {
      window.localStorage.removeItem(key);
      return;
    }
    // Over-length drafts are truncated rather than dropped, so a long
    // prompt still restores (mostly) instead of silently never persisting.
    const value =
      draft.length > DRAFT_STORAGE_MAX_LENGTH
        ? draft.slice(0, DRAFT_STORAGE_MAX_LENGTH)
        : draft;
    window.localStorage.setItem(key, value);
  } catch (error) {
    /* Storage unavailable or full — drafts are best-effort. */
  }
};

const clearFacilityDrafts = (userId, facilityId) => {
  try {
    const prefix = `${DRAFT_STORAGE_PREFIX}${userId || "anon"}:${facilityId || ""}:`;
    const staleKeys = [];
    for (let index = 0; index < window.localStorage.length; index += 1) {
      const key = window.localStorage.key(index);
      if (key && key.startsWith(prefix)) staleKeys.push(key);
    }
    staleKeys.forEach((key) => window.localStorage.removeItem(key));
  } catch (error) {
    /* ignore */
  }
};

const ComposerPanel = forwardRef(function ComposerPanel(
  {
    isStreaming,
    webSearch,
    onToggleWebSearch,
    currentPageEnabled,
    currentPageAvailable,
    currentPageTitle,
    currentPageChartCount,
    onToggleCurrentPage,
    reasoningEffort,
    onChangeReasoningEffort,
    attachments,
    attachmentsUploading,
    onRemoveAttachment,
    onAttachFiles,
    onSubmit,
    onStop,
    onVoiceStatusChange,
    companyId,
    facilityId,
    userId,
    conversationId,
    showCaption,
  },
  ref
) {
  const [draft, setDraft] = useState("");
  const textAreaRef = useRef(null);
  const fileInputRef = useRef(null);
  const draftRef = useRef(draft);
  draftRef.current = draft;
  /* Draft persistence mirrors (Wave 2.4, moved verbatim from index.js). */
  const draftSaveTimerRef = useRef(null);
  const pendingDraftSaveRef = useRef(null);
  /* Marks a programmatic clearDraft() (facility switch / streaming-blocked
     restore) so the persistence effect skips its save-scheduling cycle
     instead of treating the reset as a user edit. A plain setDraft("")
     (the after-send clear) deliberately does NOT set this marker — it must
     flow through the scheduler so writeStoredDraft removes the key. */
  const draftResetRef = useRef(false);
  const draftPersistScopeRef = useRef({ facilityId: null, conversationId: null });

  /* Voice transcript append: internal now that the recorder lives here.
     Same semantics as the old parent-side appendTranscript (leading space
     only when needed, focus + caret to end on the next frame). */
  const appendTranscript = useCallback((text) => {
    const value = String(text || "").trim();
    if (!value) return;
    setDraft((current) => {
      const base = current || "";
      const separator = base && !/\s$/.test(base) ? " " : "";
      return `${base}${separator}${value}`;
    });
    requestAnimationFrame(() => {
      const area = textAreaRef.current;
      if (area && area.resizableTextArea && area.resizableTextArea.textArea) {
        const node = area.resizableTextArea.textArea;
        node.focus();
        const end = node.value.length;
        node.setSelectionRange(end, end);
      } else if (area && area.focus) {
        area.focus();
      }
    });
  }, []);

  const transcribeClip = useCallback(
    ({ blob, signal }) =>
      transcribeFacilityAiAudio({
        companyId,
        facilityId,
        blob,
        signal,
      }).then((payload) => (payload && payload.text) || ""),
    [companyId, facilityId]
  );

  /* The recorder hook lives INSIDE the panel (see file header): its 5 Hz
     elapsedMs ticks re-render only this component. voiceRef always points
     at the latest hook object so the imperative handle below never goes
     stale when the hook recreates its callbacks (facility change etc.). */
  const voice = useVoiceInput({
    onTranscript: appendTranscript,
    transcribe: transcribeClip,
    enabled: !isStreaming,
  });
  const voiceRef = useRef(voice);
  voiceRef.current = voice;

  /* Status mirror for the parent's global push-to-talk keydown handler:
     the callback only writes the parent's ref, so status changes (and the
     5 Hz ticks) never re-render the page. */
  const onVoiceStatusChangeRef = useRef(onVoiceStatusChange);
  onVoiceStatusChangeRef.current = onVoiceStatusChange;
  useEffect(() => {
    onVoiceStatusChangeRef.current?.(voice.status);
  }, [voice.status]);

  /* Voice error toasts (moved verbatim from index.js). */
  const voiceErrorRef = useRef("");
  useEffect(() => {
    if (voice.error && voice.error !== voiceErrorRef.current) {
      voiceErrorRef.current = voice.error;
      antdMessage.error(voice.error);
      voice.clearError();
    } else if (!voice.error) {
      voiceErrorRef.current = "";
    }
  }, [voice.error, voice.clearError]);

  /* Debounced draft persistence (2.4, moved verbatim): saves always land on
     the conversation the text was typed in — switching conversations
     flushes any pending save first, and an empty draft removes the key.
     facilityId / conversationId / userId arrive as props; on a facility
     switch the child effect (which fires before the parent's facility
     effect) flushes the pending save under the OLD key, which the parent's
     clearFacilityDrafts wipe then removes — net storage state identical to
     the old discard-then-wipe ordering. */
  const flushDraftSave = useCallback(() => {
    if (draftSaveTimerRef.current) {
      clearTimeout(draftSaveTimerRef.current);
      draftSaveTimerRef.current = null;
    }
    const pending = pendingDraftSaveRef.current;
    pendingDraftSaveRef.current = null;
    if (pending) {
      writeStoredDraft(pending.userId, pending.facilityId, pending.conversationId, pending.value);
    }
  }, []);

  useEffect(() => {
    const scope = draftPersistScopeRef.current;
    const scopeChanged =
      scope.facilityId !== facilityId || scope.conversationId !== conversationId;
    scope.facilityId = facilityId;
    scope.conversationId = conversationId;

    if (scopeChanged) {
      flushDraftSave();
      // A programmatic reset whose "" lands on a scope-change run is
      // consumed here; otherwise it is consumed just below.
      if (draft === "") draftResetRef.current = false;
      return;
    }

    // Skip the scheduling cycle for a programmatic clearDraft() (facility
    // switch / streaming-blocked restore) so the reset is never mistaken
    // for a user edit that empties the new scope's stored draft.
    if (draftResetRef.current) {
      draftResetRef.current = false;
      return;
    }

    if (draftSaveTimerRef.current) clearTimeout(draftSaveTimerRef.current);
    pendingDraftSaveRef.current = {
      userId,
      facilityId,
      conversationId,
      value: draft,
    };
    draftSaveTimerRef.current = setTimeout(() => {
      draftSaveTimerRef.current = null;
      const pending = pendingDraftSaveRef.current;
      pendingDraftSaveRef.current = null;
      if (pending) {
        writeStoredDraft(pending.userId, pending.facilityId, pending.conversationId, pending.value);
      }
    }, DRAFT_SAVE_DELAY_MS);

    return () => {
      if (draftSaveTimerRef.current) {
        clearTimeout(draftSaveTimerRef.current);
        draftSaveTimerRef.current = null;
      }
    };
  }, [draft, facilityId, conversationId, userId, flushDraftSave]);

  // Save any unsaved keystrokes when the panel unmounts.
  useEffect(() => () => flushDraftSave(), [flushDraftSave]);

  /* antd 4 TextArea ref indirection: the DOM node lives at
     ref.resizableTextArea.textArea. */
  const getTextAreaNode = useCallback(() => {
    const area = textAreaRef.current;
    if (area && area.resizableTextArea && area.resizableTextArea.textArea) {
      return area.resizableTextArea.textArea;
    }
    return null;
  }, []);

  const focusComposer = useCallback(() => {
    const node = getTextAreaNode();
    if (node) {
      node.focus();
      return;
    }
    if (textAreaRef.current && typeof textAreaRef.current.focus === "function") {
      textAreaRef.current.focus();
    }
  }, [getTextAreaNode]);

  /* Paste-to-attach (moved verbatim): normalizes screenshot clipboard
     names, then hands the files to the parent's upload flow. */
  const onAttachFilesRef = useRef(onAttachFiles);
  onAttachFilesRef.current = onAttachFiles;
  const handleComposerPaste = useCallback((event) => {
    const files = event.clipboardData?.files;
    if (!files || !files.length) return;
    event.preventDefault();
    const named = Array.from(files).map((file) => {
      if (file.name && !/^image\.[a-z]+$/i.test(file.name)) return file;
      const extension = (file.type.split("/")[1] || "png").split(";")[0].replace("jpeg", "jpg");
      return new File([file], `screenshot-${Date.now()}.${extension}`, { type: file.type });
    });
    onAttachFilesRef.current(named);
  }, []);

  const submitFromComposer = useCallback(
    (event) => {
      event?.preventDefault();
      onSubmit(draftRef.current);
    },
    [onSubmit]
  );

  /* Parent API. Draft/clear semantics:
     - setDraft(value): plain programmatic set (restores after conversation
       switches; also the after-send "" clear, which must run the scheduler
       so the stored key is removed);
     - clearDraft(): programmatic reset WITH the skip marker (facility
       switch, streaming-blocked restore);
     - appendText(text): voice-style append (leading separator + focus). */
  useImperativeHandle(
    ref,
    () => ({
      focus: focusComposer,
      select: () => {
        const node = getTextAreaNode();
        if (node) {
          node.focus();
          node.select();
        } else {
          focusComposer();
        }
      },
      getDraft: () => draftRef.current,
      setDraft: (value) => setDraft(String(value ?? "")),
      clearDraft: () => {
        if (draftRef.current) draftResetRef.current = true;
        setDraft("");
      },
      appendText: appendTranscript,
      voiceStart: () => voiceRef.current?.start(),
      voiceStop: () => voiceRef.current?.stop(),
      voiceCancel: () => voiceRef.current?.cancel(),
    }),
    [focusComposer, getTextAreaNode, appendTranscript]
  );

  /* Software-keyboard avoidance (P0 fix 5): iOS Safari never resizes the
     layout viewport for the keyboard, so the bottom-anchored composer would
     sit underneath it. The hook reports the occluded strip; translating the
     whole composer up by that amount keeps it visible without disturbing
     the page grid (Android resizes-content keeps the offset at 0 →
     transform "none"). */
  const keyboardOffset = useVisualViewportOffset();

  const micTooltipTitle = isStreaming
    ? "Generating — voice input paused"
    : !voice.supported
      ? "Voice input requires HTTPS or http://localhost"
      : voice.status === "recording"
        ? "Release to transcribe"
        : "Hold to talk (or hold Ctrl/⌘+Shift)";

  return (
    <Composer
      className="facility-ai-composer"
      onSubmit={submitFromComposer}
      style={{
        transform: keyboardOffset > 0 ? `translateY(-${keyboardOffset}px)` : "none",
      }}
    >
      <ComposerInner>
        {attachments.length ? (
          <AttachmentList aria-label="Attachments">
            {attachments.map((item) => (
              <AttachmentChip key={item.localId} $failed={item.status === "failed"}>
                {item.status === "uploading" ? (
                  <LoadingOutlined aria-hidden="true" />
                ) : item.status === "failed" ? (
                  <CloseOutlined aria-hidden="true" />
                ) : (
                  <CheckCircleOutlined aria-hidden="true" />
                )}
                <span className="facility-ai-attachment-name" title={item.error || item.name}>
                  {item.name}
                </span>
                <button
                  type="button"
                  className="facility-ai-attachment-remove"
                  aria-label={`Remove ${item.name}`}
                  onClick={() => onRemoveAttachment(item.localId)}
                >
                  <CloseOutlined />
                </button>
              </AttachmentChip>
            ))}
          </AttachmentList>
        ) : null}
        <TextArea
          ref={textAreaRef}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onPaste={handleComposerPaste}
          placeholder="Ask about this facility"
          aria-label="Message input"
          autoSize={{ minRows: 1, maxRows: 6 }}
          onPressEnter={(event) => {
            if (!event.shiftKey && !isStreaming) {
              event.preventDefault();
              submitFromComposer(event);
            }
          }}
        />
        <ComposerToolbar>
          {voice.status === "recording" ? (
            <VoiceIndicator $recording>
              <VoiceDot />
              <span>
                Recording {Math.floor(voice.elapsedMs / 1000)}s · release to transcribe · Esc to
                cancel
              </span>
            </VoiceIndicator>
          ) : voice.status === "transcribing" ? (
            <VoiceIndicator>
              <LoadingOutlined aria-hidden="true" />
              <span>Transcribing…</span>
            </VoiceIndicator>
          ) : (
            <ComposerOptions>
              <WebSearchToggle
                $active={webSearch === "auto"}
                $disabled={isStreaming}
                role="button"
                tabIndex={isStreaming ? -1 : 0}
                aria-pressed={webSearch === "auto"}
                aria-label="Web search"
                onClick={() => {
                  if (!isStreaming) {
                    onToggleWebSearch();
                  }
                }}
                onKeyDown={(event) => {
                  if (!isStreaming && (event.key === "Enter" || event.key === " ")) {
                    event.preventDefault();
                    onToggleWebSearch();
                  }
                }}
              >
                <GlobalOutlined aria-hidden="true" />
                {/* Mobile (P0 fix 4a): CSS hides this label at ≤767px, leaving
                    the icon-only pill — aria-label keeps it identified. */}
                <span className="facility-ai-toggle-label facility-ai-toggle-label--websearch">
                  Web search
                </span>
              </WebSearchToggle>
              <Tooltip
                title={
                  currentPageAvailable
                    ? `Include ${currentPageChartCount} structured chart${
                        currentPageChartCount === 1 ? "" : "s"
                      } from ${currentPageTitle}`
                    : "Open Dashboard, Utility Summary, or Savings before using current page"
                }
              >
                <WebSearchToggle
                  $active={currentPageEnabled}
                  $disabled={isStreaming || !currentPageAvailable}
                  role="button"
                  tabIndex={isStreaming || !currentPageAvailable ? -1 : 0}
                  aria-pressed={currentPageEnabled}
                  aria-disabled={isStreaming || !currentPageAvailable}
                  onClick={() => {
                    if (!isStreaming && currentPageAvailable) {
                      onToggleCurrentPage();
                    }
                  }}
                  onKeyDown={(event) => {
                    if (
                      !isStreaming &&
                      currentPageAvailable &&
                      (event.key === "Enter" || event.key === " ")
                    ) {
                      event.preventDefault();
                      onToggleCurrentPage();
                    }
                  }}
                >
                  <DashboardOutlined aria-hidden="true" />
                  {/* Mobile (P0 fix 4b): the full label (whose page title is
                      unbounded) swaps for a compact "Page · N" via CSS at
                      ≤767px; the Tooltip above still carries the full
                      context on both variants. */}
                  <span className="facility-ai-toggle-label facility-ai-toggle-label--page-full">
                    Current page
                    {currentPageAvailable
                      ? ` · ${currentPageTitle} · ${currentPageChartCount}`
                      : ""}
                  </span>
                  <span className="facility-ai-toggle-label facility-ai-toggle-label--page-compact">
                    Page
                    {currentPageAvailable ? ` · ${currentPageChartCount}` : ""}
                  </span>
                </WebSearchToggle>
              </Tooltip>
              <Tooltip title="Reasoning effort controls response depth, latency, and token usage">
                <ReasoningControl>
                  <BulbOutlined aria-hidden="true" />
                  <ReasoningSelect
                    value={reasoningEffort}
                    options={REASONING_EFFORT_OPTIONS}
                    disabled={isStreaming}
                    aria-label="Reasoning effort"
                    dropdownMatchSelectWidth={false}
                    onChange={onChangeReasoningEffort}
                  />
                </ReasoningControl>
              </Tooltip>
            </ComposerOptions>
          )}
          <ComposerActions>
            <Tooltip title="Attach a file or paste a screenshot">
              <Button
                type="text"
                icon={<PaperClipOutlined />}
                aria-label="Attach a file"
                disabled={isStreaming}
                onClick={() => fileInputRef.current?.click()}
              />
            </Tooltip>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={ATTACHMENT_ACCEPT}
              style={{ display: "none" }}
              onChange={(event) => {
                onAttachFilesRef.current(Array.from(event.target.files));
                event.target.value = "";
              }}
            />
            <Tooltip title={micTooltipTitle}>
              <span>
                <Button
                  type={voice.status === "recording" ? "primary" : "text"}
                  danger={voice.status === "recording"}
                  icon={
                    voice.status === "transcribing" ? (
                      <LoadingOutlined />
                    ) : voice.status === "recording" ? (
                      <AudioMutedOutlined />
                    ) : (
                      <AudioOutlined />
                    )
                  }
                  aria-label="Hold to talk"
                  aria-pressed={voice.status === "recording"}
                  disabled={
                    !voice.supported || isStreaming || voice.status === "transcribing"
                  }
                  onMouseDown={(event) => {
                    event.preventDefault();
                    voice.start();
                  }}
                  onMouseUp={() => voice.stop()}
                  onMouseLeave={() => {
                    if (voice.status === "recording") voice.stop();
                  }}
                  onTouchStart={(event) => {
                    event.preventDefault();
                    voice.start();
                  }}
                  onTouchEnd={(event) => {
                    event.preventDefault();
                    voice.stop();
                  }}
                  onKeyDown={(event) => {
                    if ((event.key === " " || event.key === "Enter") && !event.repeat) {
                      event.preventDefault();
                      voice.start();
                    }
                  }}
                  onKeyUp={(event) => {
                    if (event.key === " " || event.key === "Enter") {
                      event.preventDefault();
                      voice.stop();
                    }
                  }}
                />
              </span>
            </Tooltip>
            {isStreaming ? (
              <Tooltip title="Stop generating (Esc)">
                <Button
                  className="facility-ai-submit-button facility-ai-stop-button"
                  shape="circle"
                  icon={<StopOutlined />}
                  onClick={onStop}
                  aria-label="Stop generating"
                />
              </Tooltip>
            ) : (
              <Tooltip title="Send message">
                <span>
                  <Button
                    className="facility-ai-submit-button"
                    type="primary"
                    htmlType="submit"
                    shape="circle"
                    icon={<SendOutlined />}
                    disabled={!draft.trim() || attachmentsUploading}
                    aria-label="Send message"
                  />
                </span>
              </Tooltip>
            )}
          </ComposerActions>
        </ComposerToolbar>
        {/* Disclaimer only before the first message (P0 fix 4f): once a
            conversation exists the caption is dropped so the composer stays
            a single compact row — index.js passes messages.length === 0. */}
        {showCaption ? (
          <div className="facility-ai-composer-caption">
            AI answers can be incomplete — verify important facility data.
          </div>
        ) : null}
      </ComposerInner>
    </Composer>
  );
});

/* Custom comparator: every prop is either a primitive (compared by value) or
   a stable useCallback / state-derived array from the parent, so stream
   frames, sidebar updates and header changes that don't touch these props
   skip the composer entirely — and keystrokes never leave this component. */
const areComposerPanelPropsEqual = (prev, next) =>
  prev.isStreaming === next.isStreaming &&
  prev.webSearch === next.webSearch &&
  prev.onToggleWebSearch === next.onToggleWebSearch &&
  prev.currentPageEnabled === next.currentPageEnabled &&
  prev.currentPageAvailable === next.currentPageAvailable &&
  prev.currentPageTitle === next.currentPageTitle &&
  prev.currentPageChartCount === next.currentPageChartCount &&
  prev.onToggleCurrentPage === next.onToggleCurrentPage &&
  prev.reasoningEffort === next.reasoningEffort &&
  prev.onChangeReasoningEffort === next.onChangeReasoningEffort &&
  prev.attachments === next.attachments &&
  prev.attachmentsUploading === next.attachmentsUploading &&
  prev.onRemoveAttachment === next.onRemoveAttachment &&
  prev.onAttachFiles === next.onAttachFiles &&
  prev.onSubmit === next.onSubmit &&
  prev.onStop === next.onStop &&
  prev.onVoiceStatusChange === next.onVoiceStatusChange &&
  prev.companyId === next.companyId &&
  prev.facilityId === next.facilityId &&
  prev.userId === next.userId &&
  prev.conversationId === next.conversationId &&
  prev.showCaption === next.showCaption;

export {
  ATTACHMENT_ACCEPT,
  ATTACHMENT_EXTENSIONS,
  ATTACHMENT_MAX_BYTES,
  AttachmentChip,
  AttachmentList,
  clearFacilityDrafts,
  readStoredDraft,
};

export default memo(ComposerPanel, areComposerPanelPropsEqual);
